import { OrdineVendita } from '../_models/OrdineVendita';
import { CampoPropagabile, modificheDi, ModificaCampo } from './propagazione-comune';

// Feature 122 (issue #3377) — US1: diff dei campi d'ordine che finiscono nello snapshot della
// pianifica, per decidere SE mostrare il dialogo di allineamento e COSA elencarvi.
//
// ⚠️ INSIEME DIVERSO da quello di `anagrafica-propagazione.ts` e `articolo-propagazione.ts`, e la
// differenza non è casuale: là i campi sono le COLONNE PROPAGABILI di un asse
// (anagrafica → ordine, articolo → ordine), qui sono i campi che l'attività collegata rispecchia
// nel proprio snapshot. Due domande diverse, due elenchi diversi.
//
// ⚠️ NESSUN GRUPPO, NESSUNA CASELLA DI SPUNTA. La scelta è ATOMICA — allinea tutto o non allineare
// nulla (FR-003, decisione D2): `NotaJson` è uno snapshot INTEGRALE, e una selezione parziale
// produrrebbe in pianifica uno stato che non è mai esistito su nessun ordine, rompendo la parità
// fra export ordini ed export pianifiche (feature 114). L'elenco serve a far VEDERE cosa cambierà,
// non a farlo scegliere pezzo per pezzo: è in SOLA LETTURA.
//
// Il nucleo puro — la regola `null ≡ '' ≡ spazi` e il tipo del diff — è condiviso con le due
// propagazioni in `propagazione-comune.ts`: quella regola è la stessa decisione, e duplicarla
// significherebbe che una correzione futura ne aggiusta una sola.

// Formatta una data per il confronto e per la resa: solo la parte giorno, che è la granularità con
// cui il server decide la riprogrammazione. Senza questo, un cambio di orario o di fuso comparirebbe
// come modifica e il dialogo si aprirebbe a vuoto.
const soloGiorno = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v);
  return s.length >= 10 ? s.substring(0, 10) : s;
};

// I campi presenti nello snapshot `NotaJson` e visibili in pianifica: note, riferimenti, dati di
// consegna, articolo e quantità.
//
// ⚠️ QUESTO ELENCO GOVERNA SOLO LA DOMANDA, MAI IL TRASPORTO. Un campo assente da qui non fa
// comparire il dialogo; il payload resta comunque integrale e lo porta insieme agli altri, perché
// senza dialogo il consenso vale «sì» (vedi `chiediAllineamentoPianifica`). La conseguenza di
// un'omissione è quindi un dialogo che NON si apre su una modifica che cambierà la pianifica —
// mai una perdita di dati.
//
// ⛔ La proprietà qui sopra dipende dal default del gate e NON è autonoma: se un giorno l'assenza di
// dialogo tornasse a valere «non allineare», ogni campo mancante da questo elenco diventerebbe una
// perdita silenziosa. I campi che la pianifica legge e che qui NON compaiono sono già oggi molti —
// `rappresentante`, `numRegistrazione`, `bis`, `statoOrdine`, `statoConsegna`, `flgTerminato`,
// `importoRiga`, `valuta`, `dataOrdine`, `pesoNetto`, `pesoLordo`, `codMagPrincipale` — quindi è il
// default a doverli coprire, non l'elenco a doverli inseguire.
export const CAMPI_SNAPSHOT_PIANIFICA: CampoPropagabile<OrdineVendita>[] = [
  // Riferimenti e cliente
  { chiave: 'rifOrdine',             etichetta: 'Riferimento ordine',   getter: o => o.rifOrdine },
  { chiave: 'ragioneSocialeCliente', etichetta: 'Cliente',              getter: o => o.ragioneSocialeCliente },
  { chiave: 'rifCliente',            etichetta: 'Rif. cliente',         getter: o => o.rifCliente },
  { chiave: 'commessa',              etichetta: 'Commessa',             getter: o => o.commessa },

  // Dati di consegna — sono anche i campi che fanno rigeocodificare l'attività a valle.
  { chiave: 'zona',                  etichetta: 'Zona',                 getter: o => o.zona },
  { chiave: 'indirizzoConsegna',     etichetta: 'Indirizzo di consegna', getter: o => o.indirizzoConsegna },
  { chiave: 'indirizzo',             etichetta: 'Indirizzo',            getter: o => o.indirizzo },
  { chiave: 'citta',                 etichetta: 'Città',                getter: o => o.citta },
  { chiave: 'provincia',             etichetta: 'Provincia',            getter: o => o.provincia },
  { chiave: 'cap',                   etichetta: 'CAP',                  getter: o => o.cap },
  { chiave: 'nazione',               etichetta: 'Nazione',              getter: o => o.nazione },

  // Contatti sede di consegna (feature 079)
  { chiave: 'riferimento',           etichetta: 'Riferimento sede',     getter: o => o.riferimento },
  { chiave: 'telefono',              etichetta: 'Telefono',             getter: o => o.telefono },
  { chiave: 'email',                 etichetta: 'Email',                getter: o => o.email },

  // Note (feature 094, 101, 104)
  { chiave: 'notaCliente',           etichetta: 'Nota cliente',         getter: o => o.notaCliente },
  { chiave: 'notaConsegna',          etichetta: 'Nota di consegna',     getter: o => o.notaConsegna },
  { chiave: 'notaAggiuntiva',        etichetta: 'Nota aggiuntiva',      getter: o => o.notaAggiuntiva },
  { chiave: 'note',                  etichetta: 'Note',                 getter: o => o.note },

  // Articolo e materiale
  { chiave: 'codiceArticolo',        etichetta: 'Codice articolo',      getter: o => o.codiceArticolo },
  { chiave: 'varianteArt',           etichetta: 'Variante articolo',    getter: o => o.varianteArt },
  { chiave: 'rifLottoProdotto',      etichetta: 'Rif. lotto prodotto',  getter: o => o.rifLottoProdotto },
  { chiave: 'codiceMateriale',       etichetta: 'Codice materiale',     getter: o => o.codiceMateriale },
  { chiave: 'varianteMat',           etichetta: 'Variante materiale',   getter: o => o.varianteMat },
  { chiave: 'rifLottoMateriale',     etichetta: 'Rif. lotto materiale', getter: o => o.rifLottoMateriale },

  // Quantità e unità di misura
  { chiave: 'quantita',              etichetta: 'Quantità',             getter: o => numero(o.quantita) },
  { chiave: 'quantitaUdM2',          etichetta: 'Quantità (UdM 2)',     getter: o => numero(o.quantitaUdM2) },
  { chiave: 'udM',                   etichetta: 'Unità di misura',      getter: o => o.udM },
  { chiave: 'udM2',                  etichetta: 'Unità di misura 2',    getter: o => o.udM2 },

  // Campi utente (feature 030/031): JSON piatto, confrontato come testo.
  { chiave: 'campiUtente',           etichetta: 'Campi utente',         getter: o => o.campiUtente },

  // ⚠️ La data di consegna è nello snapshot, ma il suo cambiamento fra due date valorizzate è una
  // RIPROGRAMMAZIONE, che ha già il proprio dialogo di spostamento: le due condizioni sono
  // mutuamente esclusive (FR-006) e la mutua esclusione è applicata dal componente, non da qui.
  // Resta in elenco perché i casi NON di riprogrammazione — riempire una data prima vuota, oppure
  // svuotarla — cambiano comunque la pianifica e devono far comparire il dialogo dati.
  { chiave: 'dataConsegnaRiga',      etichetta: 'Data di consegna',     getter: o => soloGiorno(o.dataConsegnaRiga) },
];

const numero = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

/**
 * Elenco delle modifiche fra lo stato di apertura della scheda e quello corrente, limitato ai campi
 * che la pianifica rispecchia. In SOLA LETTURA: `selezionato` è sempre `false` e non viene usato —
 * la scelta è atomica (FR-003).
 */
export function modificheSnapshotPianifica(
  prima: OrdineVendita, dopo: OrdineVendita,
): ModificaCampo[] {
  return modificheDi(CAMPI_SNAPSHOT_PIANIFICA, prima, dopo, false);
}

/**
 * Copia dei soli campi che il diff confronta, scattata all'apertura della scheda.
 *
 * ⚠️ Serve una COPIA e non un riferimento: il componente modifica `this.ordine` in luogo, quindi un
 * riferimento renderebbe il confronto sempre vuoto e il dialogo non comparirebbe mai — un difetto
 * silenzioso, esattamente della famiglia che questa feature esiste per rendere visibile.
 */
export function snapshotPerAllineamento(ordine: OrdineVendita): OrdineVendita {
  return { ...ordine } as OrdineVendita;
}
